import React, { useMemo, useState } from 'react'
import {
  Box, Button, Card, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Link, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { useSnackbar } from 'notistack'
import { C, SX, TYPE } from '../../merchants/shared/tokens'
import { KIND, errorText, upload } from '../ptlSetupApi'
import { num, tatBaseName } from '../constants'
import { Banner } from '../components/SetupUi'
import KpiStrip from '../shell/KpiStrip'
import UploadErrors from './UploadErrors'
import { DIFF_SX, tatDiff } from './tatDiff'

const changeLabel = (change, base) => ({ ADDED: 'New custom', CHANGED: 'Changed', UNCHANGED: 'No change', BACK_TO_STANDARD: `Back to ${base}` })[change]
const CHANGE_SX = {
  ADDED: { color: C.accent, bgcolor: C.accentSoft, border: `1px solid ${C.accentBorder}` },
  CHANGED: { color: C.accent, bgcolor: C.accentSoft, border: `1px solid ${C.accentBorder}` },
  UNCHANGED: { color: C.neutral, bgcolor: C.neutralSoft, border: `1px solid ${C.neutralBorder}` },
  BACK_TO_STANDARD: { color: C.ok, bgcolor: C.okSoft, border: `1px solid ${C.okBorder}` },
}

/**
 * Custom TAT upload of one merchant, D2C TAT pattern: download the current TAT → change the agreement lanes → check
 * (nothing saved) → review every lane against its base → confirm dialog → publish. The file is the merchant's whole
 * custom TAT and must have every lane (the server refuses a file with a lane missing): a lane equal to the base is on
 * the base, and a custom lane put back to the base value goes back to the base. The base is the standard, or the courier TAT the merchant is on (grid.basisCourier) with the
 * standard on that courier's missing lanes.
 */
export default function TatUpload({ merchant, grid, onDownload, onCancel, onSaved }) {
  const { enqueueSnackbar } = useSnackbar()
  const [file, setFile] = useState(null)
  const [check, setCheck] = useState(null)
  const [acknowledged, setAcknowledged] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  // a save changes TAT / EDD at once and has no rollback: the confirm needs this tick
  const [understood, setUnderstood] = useState(false)
  const [showAll, setShowAll] = useState(false)

  const clear = () => { setCheck(null); setError(''); setAcknowledged(false) }

  const run = async (confirm) => {
    setBusy(true)
    setError('')
    try {
      const res = await upload(KIND.TAT, { file, merchantId: merchant.id, confirm, acknowledge: confirm && acknowledged })
      if (!confirm) {
        setCheck(res)
      } else {
        setConfirmOpen(false)
        enqueueSnackbar('Custom TAT saved. The change is in its History.', { variant: 'success' })
        onSaved()
      }
    } catch (e) {
      setConfirmOpen(false)
      setError(errorText(e, 'Upload failed'))
    } finally {
      setBusy(false)
    }
  }

  const errors = check?.errors || []
  const counts = check?.counts || {}
  const ok = check && errors.length === 0
  const needsAck = ok && check.needsAcknowledge
  const lanes = useMemo(() => (check?.lanes || []).map((l) => ({ ...l, diff: tatDiff(l.minTat, l.maxTat, l.standardMin, l.standardMax, grid.basisCourier ? 'base' : 'standard') })), [check, grid.basisCourier])
  // What the review is for: lanes that change. Unchanged custom lanes are one click away.
  const shown = showAll ? lanes : lanes.filter((l) => l.change !== 'UNCHANGED')
  const changes = (counts.added || 0) + (counts.changed || 0) + (counts.removed || 0)
  // What custom lanes are compared with: "standard", or "base" when the merchant is on a courier's TAT.
  const base = grid.basisCourier ? 'base' : 'standard'
  const Base = base[0].toUpperCase() + base.slice(1)

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onCancel} disabled={busy} sx={{ textTransform: 'none', fontWeight: 600, color: C.inkMuted, mb: 1, px: 0.5 }}>
        Back to TAT
      </Button>
      <Typography component='h2' sx={TYPE.sectionTitle}>Upload the custom TAT</Typography>
      <Box component='ol' sx={{ m: 0, mt: 1, mb: 2.5, pl: 2.5, display: 'grid', gap: 0.5, maxWidth: 820 }}>
        <Typography component='li' sx={{ ...TYPE.rowHelp, color: C.inkBody }}>
          <Link component='button' type='button' onClick={onDownload} sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}>Download the current TAT</Link>:
          all {num(grid.cells.length)} lanes, with this merchant&apos;s custom values where it has them.
        </Typography>
        <Typography component='li' sx={{ ...TYPE.rowHelp, color: C.inkBody }}>
          Keep every lane in the file, and change B2B MIN TAT / B2B MAX TAT only on the lanes the agreement sets. A lane left at the {base} value stays on the {base}{grid.basisCourier ? ` (${tatBaseName(grid.basisCourier)}, the standard where it has no lane)` : ''}.
        </Typography>
        <Typography component='li' sx={{ ...TYPE.rowHelp, color: C.inkBody }}>Upload it here, check it and review the lanes against the {base} before saving.</Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
        <Button component='label' variant='outlined' startIcon={<UploadFileOutlinedIcon />} disabled={busy} sx={{ ...SX.ctaGhost, minHeight: 36 }}>
          {file ? file.name : 'Choose CSV file'}
          <input hidden type='file' accept='.csv' onChange={(e) => { setFile(e.target.files?.[0] || null); clear(); e.target.value = '' }} />
        </Button>
        <Typography sx={TYPE.rowHelp}>Whole working days, MIN at least 1, MAX at least MIN.</Typography>
      </Box>

      {error && <Box sx={{ mt: 2.5 }}><Banner tone='danger'>{error}</Banner></Box>}

      {check && (
        <Box sx={{ mt: 3 }} aria-live='polite'>
          <Typography component='h3' sx={{ ...TYPE.sectionTitle, mb: 1.25 }}>
            {ok ? `Review against the ${base}` : 'The file was rejected. Nothing was saved.'}
          </Typography>
          <KpiStrip ariaLabel='Upload check' items={[
            { key: 'custom', label: 'Custom lanes after', value: num(counts.customLanes), sub: `${num(counts.standardLanes)} lanes on the ${base}` },
            { key: 'faster', label: `Faster than ${base}`, value: num(counts.fasterLanes), tone: counts.fasterLanes ? 'warn' : undefined, sub: `Custom MAX below ${base} MAX` },
            { key: 'slower', label: `Slower than ${base}`, value: num(counts.slowerLanes), sub: `Custom MAX above ${base} MAX` },
            { key: 'changes', label: 'Lanes that change', value: num(changes), sub: `${num(counts.added)} new, ${num(counts.changed)} changed, ${num(counts.removed)} back to ${base}` },
            { key: 'errors', label: 'Rows with errors', value: num(errors.length), tone: errors.length ? 'danger' : 'ok', sub: errors.length ? 'Fix them and check again' : 'File is valid' },
          ]} />

          <UploadErrors errors={errors} keyLabel='Lane' fileName={`ptl_tat_${merchant.id}_errors.csv`} />

          {ok && (
            <>
              {(check.warnings || []).map((w) => <Banner key={w} tone='warn'>{w}</Banner>)}
              {needsAck && (
                <FormControlLabel sx={{ mt: -0.5, mb: 1.5 }}
                  control={<Checkbox size='small' checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />}
                  label={<Typography sx={{ fontSize: 13, color: C.inkBody }}>I have checked these lanes against the agreement and want to save them</Typography>} />
              )}
              {lanes.length === 0 ? (
                <Banner tone='info'>Every lane in the file equals the {base}, so this merchant has no custom lanes: every lane follows {tatBaseName(grid.basisCourier)}.</Banner>
              ) : (
                <Card elevation={0} sx={{ ...SX.cardFlush, overflowX: 'auto' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.75, py: 1, borderBottom: `1px solid ${C.line}`, bgcolor: C.surfaceSunken }}>
                    <Typography sx={{ ...TYPE.rowHelp, flex: 1 }}>{showAll ? 'Every custom lane after this upload' : 'Lanes this upload changes'}</Typography>
                    {lanes.some((l) => l.change === 'UNCHANGED') && (
                      <Button size='small' onClick={() => setShowAll(!showAll)} aria-pressed={showAll} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12.5 }}>
                        {showAll ? 'Show changes only' : 'Show unchanged custom lanes too'}
                      </Button>
                    )}
                  </Box>
                  <Table size='small' sx={{ minWidth: 680 }} aria-label={`Lanes against the ${base}`}>
                    <TableHead>
                      <TableRow>
                        {['Lane', Base, 'Today', 'After upload', `Against ${base}`, 'Change'].map((h) => (
                          <TableCell key={h} sx={{ ...TYPE.eyebrow, fontSize: 10, color: C.inkFaint, bgcolor: C.surfaceAlt, borderColor: C.line, whiteSpace: 'nowrap' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shown.map((l) => (
                        <TableRow key={l.zone} sx={{ '& td': { borderColor: C.lineSoft, py: 1, fontSize: 12.5 } }}>
                          <TableCell>
                            <Typography sx={TYPE.rowLabel}>{l.lane}</Typography>
                            <Typography sx={{ ...TYPE.meta, color: C.inkFaint, fontFamily: 'JetBrains Mono, monospace' }}>{l.zone}</Typography>
                          </TableCell>
                          <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: C.inkMuted, whiteSpace: 'nowrap' }}>{l.standardMin}-{l.standardMax}</TableCell>
                          <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: C.inkMuted, whiteSpace: 'nowrap' }}>
                            {l.currentMin != null ? `${l.currentMin}-${l.currentMax} custom` : Base}
                          </TableCell>
                          <TableCell sx={{ ...TYPE.value, fontSize: 12.5, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            {l.change === 'BACK_TO_STANDARD' ? `${l.minTat}-${l.maxTat} ${base}` : `${l.minTat}-${l.maxTat}`}
                          </TableCell>
                          <TableCell>{l.change === 'BACK_TO_STANDARD' ? '—' : <Box component='span' sx={{ ...SX.pill, ...DIFF_SX[l.diff.tone] }}>{l.diff.label}</Box>}</TableCell>
                          <TableCell><Box component='span' sx={{ ...SX.pill, ...CHANGE_SX[l.change] }}>{changeLabel(l.change, base)}</Box></TableCell>
                        </TableRow>
                      ))}
                      {shown.length === 0 && (
                        <TableRow><TableCell colSpan={6} sx={{ ...TYPE.rowHelp, textAlign: 'center', py: 3 }}>This file changes no lane.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1, mt: 3, pt: 2, borderTop: `1px solid ${C.line}` }}>
        <Button onClick={onCancel} disabled={busy} sx={{ textTransform: 'none', fontWeight: 600, color: C.inkMuted }}>Cancel</Button>
        {!ok ? (
          <LoadingButton variant='contained' loading={busy} disabled={!file} onClick={() => run(false)} sx={{ ...SX.cta, minHeight: 36 }}>
            {check ? 'Check again' : 'Check file'}
          </LoadingButton>
        ) : (
          <Button variant='contained' disabled={busy || changes === 0 || (needsAck && !acknowledged)} onClick={() => { setUnderstood(false); setConfirmOpen(true) }} sx={{ ...SX.cta, minHeight: 36 }}>
            Save custom TAT
          </Button>
        )}
      </Box>

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ ...TYPE.sectionTitle, fontSize: 16 }}>Save the custom TAT of {merchant.name}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...TYPE.rowHelp, fontSize: 13.5, color: C.inkBody }}>
            {num(counts.customLanes)} lane{counts.customLanes === 1 ? '' : 's'} will use the agreement TAT, the other {num(counts.standardLanes)} {tatBaseName(grid.basisCourier)}.
            The EDD of shipments priced from now on follows it; existing shipments keep theirs.
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            <Banner tone='warn'>This changes the TAT used for pricing at once and cannot be rolled back: to undo it, upload the corrected file.</Banner>
          </Box>
          <FormControlLabel sx={{ mt: 0.5 }}
            control={<Checkbox size='small' checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 13, color: C.inkBody }}>I understand this changes the TAT and cannot be rolled back</Typography>} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy} sx={{ textTransform: 'none', color: C.inkMuted }}>Cancel</Button>
          <LoadingButton variant='contained' loading={busy} disabled={!understood} onClick={() => run(true)} sx={SX.cta}>Save custom TAT</LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
